import { Service } from './services';

/**
 * Reading an hourly service correctly, in one place.
 *
 * ---------------------------------------------------------------------------
 * TWO FIELDS THAT MEAN DIFFERENT THINGS ON DIFFERENT SERVICES
 * ---------------------------------------------------------------------------
 * On a fixed service `price` is what the job costs and `minutes` is how long
 * it takes. On an hourly one `price` is the rate for a single hour and
 * `minutes` is that same hour - neither is a figure a customer should ever be
 * shown bare, because a card reading "₹499" against a three-hour booking is
 * quoting a price nobody will be charged.
 *
 * Every screen that draws a price or a duration therefore has to ask the same
 * question first, and this module is where that question is answered. It is
 * the app's half of the backend's `hourly-pricing.ts`, which does the same job
 * on the server and is the only one of the two that decides anything: what a
 * booking actually costs is computed there, from the hours this app sends, and
 * frozen. These functions exist so the number on the screen matches the number
 * on the invoice - they do not set it.
 *
 * ---------------------------------------------------------------------------
 * WHY HOURS LIVE BESIDE THE BASKET RATHER THAN INSIDE IT
 * ---------------------------------------------------------------------------
 * The basket is `Record<serviceKey, quantity>` and has been written to a
 * phone's storage in that shape since the app shipped. Widening the value to
 * an object would make every saved basket unreadable on the next launch -
 * `asCart` drops anything that is not a number - so the hours are a second map
 * keyed the same way.
 *
 * The two can only disagree in one direction: an hours entry for a service no
 * longer in the basket, which every reader here ignores and which
 * `forgetHours` clears on removal. The opposite drift is impossible, because a
 * missing entry falls back to the service's own minimum rather than to zero.
 */

/** Whether the customer picks a length for this service at all. */
export const isHourly = (service: Service): boolean =>
  service.pricingModel === 'hourly';

/** The hours this service allows, as an inclusive pair. */
export const hourRange = (service: Service): { min: number; max: number } => ({
  min: service.minHours ?? 1,
  max: service.maxHours ?? 4,
});

/** Every hour a picker should offer, low to high. */
export const hourChoices = (service: Service): number[] => {
  const { min, max } = hourRange(service);
  return Array.from({ length: Math.max(0, max - min + 1) }, (_, i) => min + i);
};

/**
 * How many hours this service is being bought for.
 *
 * Returns `undefined` for a fixed service - not 1 - because the two are
 * genuinely different and the caller has to be able to tell them apart. A
 * fixed service has no hours to send, and sending 1 would be refused by the
 * platform rather than ignored.
 *
 * On an hourly service a missing entry falls back to the minimum rather than
 * to nothing: the customer has this service in their basket, so a length has
 * to be assumed, and the smallest one is the only assumption that cannot
 * overcharge them.
 */
export const hoursFor = (
  service: Service,
  chosen: Record<string, number>,
): number | undefined => {
  if (!isHourly(service)) return undefined;

  const { min, max } = hourRange(service);
  const picked = chosen[service.key];
  if (typeof picked !== 'number' || !Number.isFinite(picked)) return min;

  /*
   * Clamped, because the bounds can move under a basket. Ops narrows an
   * hourly service to 1–2 while somebody has 4 hours of it sitting in their
   * cart, and the alternative to clamping is a checkout that fails at the
   * server with a message about a number the customer never typed.
   */
  return Math.min(max, Math.max(min, Math.round(picked)));
};

/** What one unit of this service costs, hours included. */
export const priceOf = (
  service: Service,
  chosen: Record<string, number>,
): number => service.price * (hoursFor(service, chosen) ?? 1);

/** How long one unit of this service runs, in minutes. */
export const minutesOf = (
  service: Service,
  chosen: Record<string, number>,
): number => {
  const hours = hoursFor(service, chosen);
  return hours === undefined ? service.minutes : hours * 60;
};

/** "3 hrs", or nothing at all on a fixed service. */
export const hoursLabel = (
  service: Service,
  chosen: Record<string, number>,
): string | undefined => {
  const hours = hoursFor(service, chosen);
  if (hours === undefined) return undefined;
  return hours === 1 ? '1 hr' : `${hours} hrs`;
};

/**
 * The price for a card or a list row, where there is no chosen length yet.
 *
 * ---------------------------------------------------------------------------
 * THE SUFFIX IS THE WHOLE POINT
 * ---------------------------------------------------------------------------
 * "₹499" on an hourly service is not a small imprecision - it is a different
 * number from the one the customer will be charged, shown in the place they
 * are most likely to compare on. "₹499/hr" is the same figure told truthfully,
 * and it is what stops a three-hour booking arriving at checkout looking like
 * a threefold markup nobody explained.
 *
 * Every surface that draws a price without a picker beside it reads this:
 * home rails, category shelves, search results, the similar-services rail.
 * The service page and the basket do not - they have a length to multiply by,
 * and they show the real total instead.
 *
 * `rupees` is passed in rather than imported: each screen already has its
 * own formatter, and a second one here would be the thing that eventually
 * disagrees about where the commas go.
 */
export const cardPrice = (
  service: Service,
  rupees: (value: number) => string,
): string => {
  if (service.price === 0) return 'Free';
  const shown = rupees(service.price);
  return isHourly(service) ? `${shown}/hr` : shown;
};

/**
 * The struck-through figure to draw beside {@link cardPrice}, or nothing.
 *
 * ---------------------------------------------------------------------------
 * A SECOND FUNCTION RATHER THAN A LONGER STRING
 * ---------------------------------------------------------------------------
 * A strike-through is two pieces of text with different styles, so it cannot
 * be returned inside `cardPrice`'s single string. Keeping it here anyway means
 * the `/hr` rule is applied in the same place and by the same test: an hourly
 * service reads "~~₹1,000/hr~~ ₹600/hr", and a struck figure missing the
 * suffix beside a price that has one is exactly the mismatch this avoids.
 *
 * `undefined` when there is nothing to strike, which is almost every service.
 * A free service returns nothing either - "~~₹1,000~~ Free" reads as a saving,
 * and this figure is decoration rather than a discount. See
 * `Service.wasPrice`, which is the only thing this reads and is never money.
 */
export const cardWasPrice = (
  service: Service,
  rupees: (value: number) => string,
): string | undefined => {
  if (service.wasPrice === undefined || service.price === 0) return undefined;
  const shown = rupees(service.wasPrice);
  return isHourly(service) ? `${shown}/hr` : shown;
};

/**
 * How long a card should say this service takes.
 *
 * "60 mins" is true of an hourly service in the same useless way "₹499" is: it
 * is one unit of something the customer buys several of. A row offering a
 * three-hour clean that reads "60 mins" beside "₹499/hr" describes a job that
 * is not on sale.
 *
 * The range is the honest answer where there is no chosen length - it says
 * what can be bought rather than pretending a decision has been made.
 */
export const cardDuration = (service: Service): string => {
  if (!isHourly(service)) return `${service.minutes} mins`;
  const { min, max } = hourRange(service);
  return min === max ? `${min} hr` : `${min}–${max} hrs`;
};

/**
 * Drops the hours for a service that has left the basket.
 *
 * Called on removal rather than swept periodically: a stale entry is harmless
 * while it sits there, but it comes back to life if the same service is added
 * again - and "I removed it and re-added it and it remembered 4 hours" is
 * indistinguishable from a bug even when the number is the one they picked.
 */
export const forgetHours = (
  chosen: Record<string, number>,
  serviceKey: string,
): Record<string, number> => {
  if (!(serviceKey in chosen)) return chosen;
  const next = { ...chosen };
  delete next[serviceKey];
  return next;
};

/**
 * How long this service should run after somebody adds it again.
 *
 * ---------------------------------------------------------------------------
 * ADDING AN HOURLY SERVICE TWICE IS NOT ORDERING IT TWICE
 * ---------------------------------------------------------------------------
 * A fixed service added twice is two visits, and a quantity of two is the
 * right answer. An hourly one is not: nobody wants the same service done to
 * their house twice on the same day, they want it done for longer. The basket
 * used to take the second add as a quantity, which meant two separate
 * one-hour bookings - two professionals, two slots, two of everything - where
 * the customer meant one booking of two hours.
 *
 * So a second add lengthens the line instead of duplicating it. That is also
 * the only reading that agrees with the hour chips sitting on the same row:
 * one line, one length, one control for it.
 *
 * The FIRST add is deliberately the minimum rather than `min + delta`.
 * Pressing "add" on a card means "I want this", not "I want two hours of
 * this", and starting anywhere above the minimum is the app choosing a bigger
 * bill than was asked for.
 *
 * @param current The hours already chosen, or undefined when this is the
 *   first add. `forgetHours` is what makes absence mean "not in the basket".
 */
export const hoursAfterAdd = (
  service: Service,
  current: number | undefined,
  delta = 1,
): number => {
  const { min, max } = hourRange(service);
  if (current === undefined) return min;
  return Math.min(max, Math.max(min, current + delta));
};
